﻿const DIMENSIONS = [
  {
    key: "task_exposure",
    label: "任务暴露度",
    weight: "30%",
    color: "#f97316",
    formula: "(B1×0.22 + B2×0.28 + B3×0.18 + B4×0.12 + B5×0.10 + B6×0.10) × 学历调整因子 × 经验调整因子",
    questions: "B1 文字任务比例 / B2 编程比例 / B3 体力比例 / B4 科学判断 / B5 重复性任务 / B6 产出形式",
    source: "OpenAI(2023) Table 5: Writing +0.467***, Programming +0.623***, Routine Manual -0.111***, Science -0.230***, Critical Thinking -0.196***",
    sourceUrl: "https://arxiv.org/abs/2303.10130",
  },
  {
    key: "skill_vulnerability",
    label: "技能脆弱度",
    weight: "25%",
    color: "#ef4444",
    formula: "C1×0.35 + C2×0.25 + C3×0.25 + C4×0.15",
    questions: "C1 AI增值技能(多选) / C2 培训频率 / C3 AI工具使用频率 / C4 手工技能重要性",
    source: "WEF 2025: 63%雇主视技能差距为最大障碍; AI+大数据、网安、技术素养位列增长技能前三",
    sourceUrl: "https://www.weforum.org/publications/the-future-of-jobs-report-2025/",
  },
  {
    key: "industry_impact",
    label: "行业冲击度",
    weight: "20%",
    color: "#eab308",
    formula: "A2行业冲击分×0.40 + D1公司AI采纳×0.25 + D2公司规模×0.15 + D3数字化成熟度×0.20",
    questions: "A2 所在行业(15行业预设分) / D1 公司AI使用 / D2 公司规模 / D3 数字化程度",
    source: "McKinsey(2023) Exhibit 4: 行业AI影响分布; WEF 2025 Figure 1.2: 86%雇主预期AI转变业务",
    sourceUrl: "https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier",
  },
  {
    key: "adaptability",
    label: "适应弹性",
    weight: "15%",
    color: "#22c55e",
    note: "反向计分：分数越低 = 适应力越强",
    formula: "E1×0.30 + E2×0.25 + E3×0.20 + E4×0.15 + C5×0.10",
    questions: "E1 新技术态度 / E2 备选职业方向 / E3 AI发展态度 / E4 行业趋势关注 / C5 转型信心",
    source: "WEF 2025: 85%雇主计划优先提升员工技能; 已培训工人比例从41%(2023)升至50%(2025)",
    sourceUrl: "https://www.weforum.org/publications/the-future-of-jobs-report-2025/",
  },
  {
    key: "outlook",
    label: "发展前景",
    weight: "10%",
    color: "#3b82f6",
    formula: "f(A1职业类别) — 查表映射",
    questions: "A1 职业类别 → WEF 2025岗位增长/衰退分类查询",
    source: "WEF 2025: 增长型(大数据/AI/护理/可再生能源) vs 衰退型(数据录入/银行柜员/邮政)",
    sourceUrl: "https://www.weforum.org/publications/the-future-of-jobs-report-2025/",
  },
];

const RISK_LEVELS = [
  { range: "0-25", level: "低风险", color: "#22c55e", meaning: "你的职业在AI时代相对安全，继续保持学习和关注" },
  { range: "26-45", level: "中低风险", color: "#3b82f6", meaning: "有一定影响但可控，建议主动学习AI工具" },
  { range: "46-60", level: "中等风险", color: "#eab308", meaning: "需要认真对待，建议制定技能提升计划" },
  { range: "61-75", level: "高风险", color: "#f97316", meaning: "你的岗位可能在3-5年内发生重大变化，需要尽快行动" },
  { range: "76-100", level: "极高风险", color: "#ef4444", meaning: "你的岗位面临较大的AI替代压力，建议立即制定职业提升计划" },
];

export function Methodology({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-screen max-w-4xl mx-auto px-8 pt-20 pb-16 space-y-12">
      {/* Back navigation */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-[#94a3b8] hover:text-[#f1f5f9] transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        返回首页
      </button>

      {/* Header */}
      <div className="text-center space-y-3">
        <h1 className="font-[family-name:var(--font-heading)] text-4xl font-bold text-[#f1f5f9]">
          评估方法
        </h1>
        <p className="text-[#94a3b8] max-w-2xl mx-auto leading-relaxed">
          本评估采用五维度量化模型，每个维度的权重和系数均来自公开发表的学术研究和权威机构报告。
          所有数据来源可追溯、可验证、可下载。
        </p>
      </div>

      {/* Core Formula */}
      <div className="p-6 rounded-xl bg-[#1e293b] border border-[#334155] text-center" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h2 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-4">核心评分公式</h2>
        <div className="font-mono text-lg text-[#e2e8f0] bg-[#0f172a] rounded-lg px-6 py-4 inline-block">
          总分 = <span style={{ color: "#f97316" }}>0.30 × 任务暴露度</span> + <span style={{ color: "#ef4444" }}>0.25 × 技能脆弱度</span> + <span style={{ color: "#eab308" }}>0.20 × 行业冲击度</span> + <span style={{ color: "#22c55e" }}>0.15 × 适应弹性</span> + <span style={{ color: "#3b82f6" }}>0.10 × 发展前景</span>
        </div>
        <p className="text-xs text-[#64748b] mt-3">结果经交叉校验规则调整（±0~15分），最终 clamp 至 0-100 区间</p>
      </div>

      {/* Five Dimensions */}
      <section className="space-y-6">
        <h2 className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#f1f5f9]">五维度详解</h2>
        {DIMENSIONS.map((dim) => (
          <div
            key={dim.key}
            className="p-5 rounded-xl bg-[#1e293b] border border-[#334155]"
            style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)", borderLeftWidth: "3px", borderLeftColor: dim.color }}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9]">
                {dim.label}
              </span>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded"
                style={{ color: dim.color, backgroundColor: dim.color + "18" }}
              >
                权重 {dim.weight}
              </span>
              {dim.note && (
                <span className="text-xs text-[#94a3b8]">{dim.note}</span>
              )}
            </div>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-[#64748b]">计算公式：</span>
                <span className="text-[#cbd5e1] font-mono text-xs">{dim.formula}</span>
              </div>
              <div>
                <span className="text-[#64748b]">相关题目：</span>
                <span className="text-[#94a3b8]">{dim.questions}</span>
              </div>
              <div>
                <span className="text-[#64748b]">数据依据：</span>
                <span className="text-[#94a3b8]">{dim.source}</span>
                {" "}
                <a
                  href={dim.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#60a5fa] hover:underline text-xs"
                >
                  [论文链接]
                </a>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Risk Levels */}
      <section className="space-y-4">
        <h2 className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#f1f5f9]">风险等级划分</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#334155]">
                <th className="text-left py-3 px-4 text-[#64748b] font-medium">分数区间</th>
                <th className="text-left py-3 px-4 text-[#64748b] font-medium">风险等级</th>
                <th className="text-left py-3 px-4 text-[#64748b] font-medium">含义</th>
              </tr>
            </thead>
            <tbody>
              {RISK_LEVELS.map((rl) => (
                <tr key={rl.level} className="border-b border-[#1e293b]">
                  <td className="py-3 px-4 text-[#cbd5e1] font-mono">{rl.range}</td>
                  <td className="py-3 px-4">
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded"
                      style={{ color: rl.color, backgroundColor: rl.color + "18" }}
                    >
                      {rl.level}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-[#94a3b8]">{rl.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Cross Checks */}
      <section className="space-y-4">
        <h2 className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#f1f5f9]">交叉校验机制</h2>
        <div className="p-5 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
          <p className="text-sm text-[#94a3b8] mb-4 leading-relaxed">
            为提高评分准确性，系统内置三道一致性校验，检测用户的自我认知与实际答题间的矛盾：
          </p>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-2">
              <span className="text-[#f97316] shrink-0">&bull;</span>
              <span className="text-[#cbd5e1]">
                <strong>B1+B2 vs V2 认知校准：</strong>
                若文字/编程任务占比高但用户低估 AI 影响范围，风险上修 +8
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#22c55e] shrink-0">&bull;</span>
              <span className="text-[#cbd5e1]">
                <strong>B3 vs V2 体力任务校准：</strong>
                若体力/动手工作为主但焦虑程度高，风险下修 -5
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#22c55e] shrink-0">&bull;</span>
              <span className="text-[#cbd5e1]">
                <strong>V1 vs B3+B4 人际一致性：</strong>
                若人际密集但填报面对面频率低，可能漏评人际技能，风险下修 -8
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* Data Source References */}
      <section className="space-y-4">
        <h2 className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[#f1f5f9]">参考文献</h2>
        <div className="space-y-3 text-sm">
          {[
            {
              id: "[1]",
              text: "Eloundou, T., Manning, S., Mishkin, P., & Rock, D. (2023). \"GPTs are GPTs: An Early Look at the Labor Market Impact Potential of Large Language Models.\" OpenAI Working Paper.",
              url: "https://arxiv.org/abs/2303.10130",
              note: "职业暴露度评分、技能-暴露回归分析、Job Zone教育水平分析",
            },
            {
              id: "[2]",
              text: "Chui, M., Hazan, E., Roberts, R., et al. (2023). \"The Economic Potential of Generative AI: The Next Productivity Frontier.\" McKinsey Global Institute.",
              url: "https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier",
              note: "行业影响评估、业务职能自动化比例、经济价值预测",
            },
            {
              id: "[3]",
              text: "World Economic Forum. (2025). \"Future of Jobs Report 2025.\"",
              url: "https://www.weforum.org/publications/the-future-of-jobs-report-2025/",
              note: "2025-2030岗位增减预测、技能变化趋势、雇主策略数据",
            },
            {
              id: "[4]",
              text: "World Economic Forum. (2023). \"Future of Jobs Report 2023.\"",
              url: "https://www.weforum.org/publications/the-future-of-jobs-report-2023/",
              note: "2023-2027预测基线、技能不稳定性趋势",
            },
            {
              id: "[5]",
              text: "O*NET Resource Center. \"O*NET Database 29.3.\" U.S. Department of Labor.",
              url: "https://www.onetcenter.org/database.html",
              note: "1,016个标准职业的任务、技能、知识、工作活动数据",
            },
          ].map((ref) => (
            <div key={ref.id} className="p-4 rounded-lg bg-[#1e293b] border border-[#334155]">
              <span className="text-[#3b82f6] font-bold text-xs">{ref.id} </span>
              <span className="text-[#cbd5e1]">{ref.text}</span>
              <div className="mt-1 ml-5 flex items-center gap-3">
                <span className="text-[#64748b] text-xs">{ref.note}</span>
                <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-[#60a5fa] hover:underline text-xs">
                  [下载论文]
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Limitations */}
      <section className="p-5 rounded-xl bg-[#1e293b] border border-[#334155]" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
        <h2 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[#f1f5f9] mb-3">评估局限性</h2>
        <ul className="space-y-2 text-sm text-[#94a3b8]">
          <li>&bull; 本评估基于公开学术研究和机构报告，评分为统计概率性结果，不构成职业决策的唯一依据</li>
          <li>&bull; 模型假设"学历越高暴露度越高"、"经验越深保护越强"，但这些是统计规律，个体差异可能很大</li>
          <li>&bull; AI技术发展速度和政策环境变化（如监管、税收、工会力量）不在模型考量范围内</li>
          <li>&bull; 评分侧重 LLM/生成式 AI 的影响，未完全涵盖机器人、自动驾驶等非语言类自动化技术</li>
        </ul>
      </section>

      {/* Back to assessment */}
      <div className="text-center pt-8">
        <button
          onClick={onBack}
          className="px-8 py-3 rounded-full bg-[#3b82f6] text-white font-semibold hover:bg-[#2563eb] transition-colors"
          style={{ boxShadow: "0 0 24px -6px rgba(59,130,246,0.3)" }}
        >
          返回评估
        </button>
      </div>
    </div>
  );
}